<template lang="pug">
	section.section.main-section(v-if="users.length > 0")
		.container
			title-comp(:name="$t('title.analytics')")
			.box.graph-box
				.columns.is-centered.is-vcentered
					.column: .field
						label.label {{ $t('label.time_interval') }}
						.control
							.select
								select(v-model="timeInterval")
									option(value="month") {{ $t('select.time_month') }}
									option(value="week") {{ $t('select.time_week') }}
									option(value="day") {{ $t('select.time_day') }}
					.column
						.field(v-if="timeInterval === 'month'")
							label.label {{ $t('label.time_start_month') }}
							.control
								vue-monthly-picker(
									v-model="startMonth"
									:month-labels="monthLabels"
									:date-format="'MM / YYYY'"
									:max="new Date()"
									:selected-background-color="'#ff7f2a'")
						.field(v-else-if="timeInterval === 'week'")
							label.label {{ $t('label.time_start_week') }}
							week-picker-comp(v-model="startWeek" :id="'start-week'" :week-disabled="weekStartDisable")
						.field(v-else-if="timeInterval === 'day'")
							label.label {{ $t('label.time_start_day') }}
							day-picker-comp(v-model="startDate" :id="'start-day'")

					.column
						.field(v-if="timeInterval === 'month'")
							label.label {{ $t('label.time_end_month') }}
							.control
								vue-monthly-picker(
									v-model="endMonth"
									:month-labels="monthLabels"
									:date-format="'MM / YYYY'"
									:min="startMonth"
									:max="new Date()"
									:selected-background-color="'#ff7f2a'")
						.field(v-else-if="timeInterval === 'week'")
							label.label {{ $t('label.time_end_week') }}
							week-picker-comp(v-model="endWeek" :id="'end-week'" :min="startWeek" :week-disabled="weekEndDisable")
						.field(v-else-if="timeInterval === 'day'")
							label.label {{ $t('label.time_end_day') }}
							day-picker-comp(v-model="endDate" :id="'end-day'" :min="startDate")

					.column.no-grow
						.field
							label.label.is-hidden-mobile &nbsp;
							.control.has-text-centered
								button.button.is-primary(type="button" v-bind:class="buttonClass" @click="loadGraph")
									| {{ $t('button.update') }}
									span.icon.feather
										chevron-right-icon

				.margin-top-md
					p.has-text-centered(v-if="timeInterval === 'month'")
						| {{ $t('analytics.frequency_month_help') }}
					p.has-text-centered(v-if="timeInterval === 'week'")
						| {{ $t('analytics.frequency_week_help') }}
					p.has-text-centered(v-if="timeInterval === 'day'")
						| {{ $t('analytics.frequency_day_help') }}

				.margin-top-sm
					timeline-comp(:dataset="frequency" :unit="timeInterval")

	section.section.main-section(v-else)
		.container.container-narrow
			title-comp(:name="$t('title.analytics')")

			article.message.is-warning
				.message-body
					| {{ $t('analytics.empty') }}

			p.control.has-text-centered.margin-bottom-md
				button.button.is-primary.button-right-icon(type="button" @click="resetAndLoad")
					| {{ $t('button.refresh') }}
					span.icon.feather
						refresh-cw-icon

</template>

<script>
import TitleComponent from '../components/TitleComponent.vue'
import WeekPickerComponent from '../components/WeekPickerComponent.vue'
import DayPickerComponent from '../components/DayPickerComponent.vue'
import TimelineComponent from '../components/TimelineComponent.vue'
import VueMonthlyPicker from 'vue-monthly-picker'
import { ChevronRightIcon, RefreshCwIcon } from 'vue-feather-icons'

export default {
	components: {
		TitleComp     : TitleComponent,
		WeekPickerComp: WeekPickerComponent,
		DayPickerComp : DayPickerComponent,
		TimelineComp  : TimelineComponent,
		VueMonthlyPicker,
		ChevronRightIcon,
		RefreshCwIcon
	},
	data() {
		const months = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']

		return {
			emptyPage       : false,
			errorPage       : false,
			monthLabels     : months.map(m => this.$t('time.month.' + m)),
			timeInterval    : 'month',
			startMonth      : null,
			endMonth        : null,
			startWeek       : null,
			endWeek         : null,
			startDate       : null,
			endDate         : null,
			weekStartDisable: [0,2,3,4,5,6],
			weekEndDisable  : [1,2,3,4,5,6],
			activeTab       : 'users',
			lastData        : null
		}
	},
	computed: {
		accessToken() {
			return this.$store.state.auth.accessToken
		},
		loading() {
			return this.$store.state.analytics.loading
		},
		frequency() {
			return this.$store.state.analytics.frequency
		},
		users() {
			return this.$store.getters['invitecode/users']
		},
		buttonClass() {
			return { 'is-loading': this.loading }
		},
		tabUsersClass() {
			return { 'is-active': this.activeTab === 'users' }
		},
		tabFreqClass() {
			return { 'is-active': this.activeTab === 'frequency' }
		}
	},
	mounted() {
		//this.$meta().setOptions({ title: this.$t("title.ranks") + ' | ' + this.$t("title.home") })
		if (Array.isArray(this.users) && this.users.length === 0) {
			return this.resetAndLoad()
		}
		this.loadGraph()
	},
	methods: {
		changeTab(ev) {
			this.activeTab = ev.target.getAttribute('tab')
			this.emptyPage = false
			this.errorPage = false
			this.loadGraph()
		},
		resetAndLoad() {
			this.timeInterval = 'month'
			this.lastData = null
			this.emptyPage = false
			this.errorPage = false

			this.$store.dispatch('invitecode/getInviteCodes', this.accessToken).then(() => {
				this.loadGraph()
			}).catch(err => {
				if (err.response && err.response.status) {
					console.warn(err.response.status)
				}
			})
		},
		loadGraph() {
			switch (this.timeInterval) {
				case 'month':
					if (this.lastData === null || this.lastData.interval !== 'month' || this.lastData.start !== this.startMonth || this.lastData.end !== this.endMonth) {
						return this.updateGraph({
							'interval': 'month',
							'start'   : this.startMonth,
							'end'     : this.endMonth
						})
					}
					break
				case 'week':
					const startWeek = this.startWeek ? this.startWeek : null
					const endWeek   = this.endWeek ? this.endWeek : null
					if (this.lastData === null || this.lastData.interval !== 'week' || this.lastData.start !== startWeek || this.lastData.end !== endWeek) {
						return this.updateGraph({
							'interval': 'week',
							'start'   : startWeek,
							'end'     : endWeek
						})
					}
					break
				case 'day':
					if (this.lastData === null || this.lastData.interval !== 'day' || this.lastData.start !== this.startDate || this.lastData.end !== this.endDate) {
						return this.updateGraph({
							'interval': 'day',
							'start'   : this.startDate,
							'end'     : this.endDate
						})
					}
					break
			}
		},
		updateGraph(data) {
			this.lastData = data
			this.$store.dispatch('analytics/getFrequency', { 'token': this.accessToken, 'data': data }).then(() => {
				this.emptyPage = false
				this.errorPage = false
			}).catch(err => {
				if (err.response && err.response.status) {
					switch (err.response.status) {
						case 404:
							this.emptyPage = true
							break
						case 500:
							this.errorPage = true
							break
						default:
							console.warn(err.response.status)
					}
				}
			})
		}
	},
	watch: {
		timeInterval() {
			this.loadGraph()
		}
	}
}
</script>
